import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ConvertedToItemQuery, ConvertedToItemQueryVariables} from 'metadata/__generated/ConvertedToItemQuery';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';

type ConvertedBioentity = ConvertedToItemQuery['bioentity'];

export const CONVERTED_TO_ITEM_QUERY = gql`
  query ConvertedToItemQuery($bioentityId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    bioentity(id: $bioentityId) {
      ... on RegistryItemInterface {
        id
        convertedToItem {
          ... on RegistryItemInterface {
            id
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ConvertedToItemProps {
  bioentity: ConvertedBioentity;
}

function ConvertedToItemField(props: ConvertedToItemProps): JSX.Element | null {
  if (isInaccessible(props.bioentity)) {
    return (
      <div>You do not have access to view the converted item. Please ask your administrator for access.</div>
    );
  } else if (props.bioentity.convertedToItem != null) {
    return (
      <div className={`${css(styles.convertedToItem)} metadata-convertedToItem-header`}>
        {'Converted to: '}
        <BioentityChip id={props.bioentity.convertedToItem.id} size={ChipSize.SMALL} />
      </div>
    );
  } else {
    return null;
  }
}

interface QueryingConvertedToItemFieldProps {
  fileId: string;
}

export default function QueryingConvertedToItemField(
  props: QueryingConvertedToItemFieldProps
): JSX.Element | null {
  // TODO: when we start supporting schematized item conversion beyond bioentities, we will need to
  // update this query to query for a variable more general than `bioentity`.
  const result = useBenchlingQuery<ConvertedToItemQuery, ConvertedToItemQueryVariables>(
    CONVERTED_TO_ITEM_QUERY,
    {
      variables: {bioentityId: props.fileId},
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );

  if (result.type === QueryResultType.PENDING) {
    return null;
  } else if (result.type === QueryResultType.SUCCESS) {
    return <ConvertedToItemField bioentity={result.data.bioentity} />;
  } else {
    return null;
  }
}

const styles = StyleSheet.create({
  convertedToItem: {
    paddingTop: '10px',
  },
});
